import './ContactPointChart.scss';

import { ContactPoint, ContactPointGroup } from 'types/CodegenReportTypes';

import StrikeZone, {
  generateRandomStrikeZonePoints,
  getRelativeStrikeZonePointX,
  getRelativeStrikeZonePointY,
} from 'components/Charts/StrikeZone/StrikeZone';
import { memo, useCallback, useMemo } from 'react';
import { useChartDataDisplay, useGetUnitSystem } from 'hooks/helperHooks';
import Legend, { LegendItem } from 'components/Charts/Legend/Legend';
import { Tooltip, Typography } from '@trackman/web-shared-components';
import { mergeFoulBallVariants, meterToFeet, playResults } from 'utilities/constants';
import { ReactComponent as BasePlateTop } from 'svg/BasePlateTop.svg';
import { playResultToColor } from 'utilities/colorHelpers';
import { UnitSystem } from 'types/StartupTypes';

const playResultLegendItems: LegendItem[] = playResults.map((playResult) => ({
  label: playResult,
  dotProps: { color: playResultToColor[playResult] },
}));

interface ContactPointChartProps {
  chartData: ContactPointGroup[] | undefined;
  showBufferZone?: boolean;
  showStrikeZoneGrid?: boolean;
  groupLabelsToSimulate: string[];
  getGroupLabelColor: (groupLabel: string, point?: ContactPoint) => string;
  getGroupLabelTextColor: (groupLabel: string, point?: ContactPoint) => string;
  isLoading: boolean;
}

const strikeZoneTopViewRangeX = [-8.5, 8.5];
const strikeZoneTopViewRangeY = [0, 42.5];

function getRelativeStrikeZoneTopViewPointX(x: number): number {
  return ((x - strikeZoneTopViewRangeX[0]) / (strikeZoneTopViewRangeX[1] - strikeZoneTopViewRangeX[0])) * 100;
}

function getRelativeStrikeZoneTopViewPointY(y: number): number {
  return ((y - strikeZoneTopViewRangeY[0]) / (strikeZoneTopViewRangeY[1] - strikeZoneTopViewRangeY[0])) * 100;
}

function generateRandomStrikeZoneTopViewPoints(amount: number): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < amount; i++) {
    points.push({
      x: Math.random() * (strikeZoneTopViewRangeX[1] - strikeZoneTopViewRangeX[0]) + strikeZoneTopViewRangeX[0],
      y: Math.random() * (strikeZoneTopViewRangeY[1] - strikeZoneTopViewRangeY[0]) + strikeZoneTopViewRangeY[0],
    });
  }
  return points;
}

function getRandomPlayResult(): string {
  return playResults[Math.floor(Math.random() * playResults.length)];
}

interface ContactPointDisplayProps {
  point: ContactPoint;
  color: string;
  textColor: string;
  getX: (x: number) => number;
  getY: (y: number) => number;
  unitSystem: UnitSystem;
  style?: React.CSSProperties;
}

const ContactPointDisplay = memo(({ point, color, textColor, getX, getY, unitSystem, style }: ContactPointDisplayProps) => {
  const positionX = getX(point.x * -1 * (unitSystem === 'Metric' ? meterToFeet : 1));
  const positionY = getY(point.y * (unitSystem === 'Metric' ? meterToFeet : 1));

  return (
    <Tooltip title={`#${point.pitchOfGame} (${point.x.toFixed(2)}, ${point.y.toFixed(2)})`}>
      <div
        className='StrikeZone-point'
        style={{
          left: `${positionX}%`,
          bottom: `${positionY}%`,
          backgroundColor: color + '80',
          borderColor: color,
          display: 'grid',
          placeItems: 'center',
          padding: 1,
          ...style,
        }}
      >
        <Typography variant={'label'} style={{ color: textColor, lineHeight: 0 }}>
          {point.pitchOfGame}
        </Typography>
      </div>
    </Tooltip>
  );
});

const ContactPointChart = ({
  chartData,
  showBufferZone,
  showStrikeZoneGrid,
  groupLabelsToSimulate,
  getGroupLabelColor,
  getGroupLabelTextColor,
  isLoading,
}: ContactPointChartProps) => {
  const simulateChartData = useCallback((isLoading: boolean): ContactPointGroup[] => {
    return isLoading
      ? groupLabelsToSimulate.map((label, i) => ({
          label,
          batterViewPoints: generateRandomStrikeZonePoints(7).map(({ x, y }, j) => ({
            x,
            y,
            pitchOfGame: i * 7 + j + 1,
            playResult: getRandomPlayResult(),
          })),
          topViewPoints: generateRandomStrikeZoneTopViewPoints(7).map(({ x, y }, j) => ({
            x,
            y,
            pitchOfGame: i * 7 + j + 1,
            playResult: getRandomPlayResult(),
          })),
        }))
      : [];
  }, []);

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  const unitSystem = useMemo(() => (isLoading ? 'Imperial' : useGetUnitSystem()), [isLoading]);

  const groupLabels = useMemo(
    () => Array.from(new Set(chartDataDisplay.map((group) => mergeFoulBallVariants(group.label)).filter((label) => label !== 'InPlay'))),
    [chartDataDisplay]
  );

  const playResults = useMemo(
    () =>
      Array.from(
        chartDataDisplay.flatMap((group) =>
          [...group.batterViewPoints, ...group.topViewPoints].map((x) => x.playResult).filter((x) => x !== 'Undefined')
        )
      ),
    [chartDataDisplay]
  );

  const legendItems: LegendItem[] = useMemo(
    () => [
      ...groupLabels.map((groupLabel) => ({
        label: groupLabel,
        dotProps: { color: getGroupLabelColor(groupLabel, undefined) },
      })),
      ...playResultLegendItems.filter((item) => playResults.includes(item.label)),
    ],
    [groupLabels, playResults]
  );

  return (
    <div className='ContactPointChart-container'>
      <div className='ContactPointChart-batter-view'>
        <StrikeZone basePlateOrientation={'BatterSide'} showBufferZone={showBufferZone} showStrikeZoneGrid={showStrikeZoneGrid}>
          {chartDataDisplay.map((group) =>
            group.batterViewPoints.map((point) => (
              <ContactPointDisplay
                key={`batterview-point-${point.pitchOfGame}`}
                point={point}
                color={getGroupLabelColor(group.label, point)}
                textColor={getGroupLabelTextColor(group.label, point)}
                getX={getRelativeStrikeZonePointX}
                getY={getRelativeStrikeZonePointY}
                unitSystem={unitSystem}
              />
            ))
          )}
        </StrikeZone>
        <Legend items={legendItems} disableSorting={true} />
      </div>
      <div className='ContactPointChart-top-view'>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: '-35%', width: '2px', backgroundColor: '#a0a0a0' }}></div>
        <div style={{ position: 'absolute', top: 0, bottom: 0, right: '-35%', width: '2px', backgroundColor: '#a0a0a0' }}></div>
        {chartDataDisplay.map((group) =>
          group.topViewPoints.map((point) => (
            <ContactPointDisplay
              key={`topview-point-${point.pitchOfGame}`}
              point={point}
              color={getGroupLabelColor(group.label, point)}
              textColor={getGroupLabelTextColor(group.label, point)}
              getX={getRelativeStrikeZoneTopViewPointX}
              getY={getRelativeStrikeZoneTopViewPointY}
              unitSystem={unitSystem}
              style={{ height: '6.5%' }}
            />
          ))
        )}
        <BasePlateTop className='ContactPointChart-base-plate-top' />
      </div>
    </div>
  );
};

export default ContactPointChart;
